// @flow

import React from 'react';
import _ from 'lodash';
import bemify from 'bemify-js';

import AccountChoice from 'app/common/components/AccountChoice';
import AvatarOrTeamLogoContainer from 'app/common/components/AvatarOrTeamLogoContainer';
import DropdownMenu from 'app/common/components/DropdownMenu';
import DropdownStateful from 'app/common/components/DropdownStateful';
import Icon from 'app/common/components/Icon';
import TruncatedText from 'app/common/components/TruncatedText';
import createRenderCallback from 'app/common/components/createRenderCallback';
import { Div } from 'app/common/components/AccessibleElement';
import 'app/zap-mover/components/ZapMoverAccountDropdown.scss';

import type { AccountType } from 'app/zap-mover/types';

const bem = bemify('zap-mover-account-dropdown');

type AccountDropdownToggleProps = {
  canOpen: boolean,
  id: number,
  name: string,
  planName: string,
  onClick?: () => void,
};

const AccountDropdownToggle = (props: AccountDropdownToggleProps) => {
  const className = bem('__toggle', {
    '--can-open': props.canOpen,
  });
  const dropdownIcon = !props.canOpen ? null : (
    <Icon className={bem('__dropdown-icon')} type="arrow-down" />
  );
  return (
    <Div className={className} onClick={props.onClick}>
      <AvatarOrTeamLogoContainer
        accountId={props.id}
        className={bem('__account-logo')}
        size={32}
      />
      <div className={bem('__toggle-text')}>
        <TruncatedText block={true} className={bem('__account-name')}>
          {props.name}
        </TruncatedText>
        <TruncatedText block={true} className={bem('__plan-name')}>
          {props.planName}
        </TruncatedText>
      </div>
      {dropdownIcon}
    </Div>
  );
};

AccountDropdownToggle.defaultProps = {
  canOpen: false,
  name: 'Choose an Account',
  planName: '',
};

const AccountDropdownMenu = props => {
  return (
    <DropdownMenu
      {...props}
      className={bem('__menu')}
      MenuItem={AccountChoice}
    />
  );
};

type ZapMoverAccountDropdownProps = {
  accounts: Array<AccountType>,
  onChooseAccount: number => void,
  value?: number,
};

class ZapMoverAccountDropdown extends React.Component<ZapMoverAccountDropdownProps> {
  onChooseAccount = (id: number, didConfirm: boolean) => {
    if (didConfirm) {
      this.props.onChooseAccount(id);
    }
  };

  render() {
    const { props } = this;
    const currentAccount = _.find(props.accounts, { id: props.value }) || {};
    const items = props.accounts.map(account => ({
      ...account,
      isActive: account.id === props.value,
      label: account.name,
      value: account.id,
    }));
    const hasMultipleChoices = items.some(item => item.id !== props.value);

    return hasMultipleChoices ? (
      <DropdownStateful
        className={bem()}
        items={items}
        onChoose={this.onChooseAccount}
        renderMenu={createRenderCallback(AccountDropdownMenu)}
        renderToggle={createRenderCallback(AccountDropdownToggle, {
          ...currentAccount,
          canOpen: true,
        })}
      />
    ) : (
      <AccountDropdownToggle {...currentAccount} />
    );
  }
}

export default ZapMoverAccountDropdown;
